import { Box, Text } from '@chakra-ui/react';
import cn from 'classnames';
import { produce } from 'immer';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createParser, Options, useQueryState } from 'nuqs';
import React, { useRef, useTransition } from 'react';

import Image from '@/app/_components/Image';
import {
  BodyTypeList,
  ColourTypeList,
  DoorTypeList,
} from '@/services/filters/new-filters.service';
import { getImageLink } from '@/services/link.service';

import { useLandingAwareClear } from '../../hooks/useLandingAwareClear';
import { NewFilter } from '../new-filter/new-filter';
import { isFilterEqual, useQueryNonce, useResetPage } from '../utils';

import styles from './new-body.module.scss';

export const NewBody: React.FC<{
  bodyTypes: BodyTypeList;
  colourTypes: ColourTypeList;
  doorTypes: DoorTypeList;
  count: number;
  searchQuery?: string; // New prop for search query
  setSearchQuery?: (value: string | null) => void;
}> = ({ bodyTypes = [], colourTypes, doorTypes, count, searchQuery, setSearchQuery }) => {
  const params = useParams();
  const { generate } = useQueryNonce();
  const resetPage = useResetPage();
  const landingAwareClear = useLandingAwareClear();
  const [_, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams()
  // const autoSelectedBodyIdsRef = useRef<Set<number>>(new Set());
  // const autoSelectedColourIdsRef = useRef<Set<number>>(new Set());
  // const previousSearchQueryRef = useRef<string>();
  const searchClearedByUserRef = useRef(false);

  /* --------------------------------------------------
   * LANDING BODYTYPE (APPLY ONLY ON FIRST LOAD)
   * -------------------------------------------------- */

  const landingBodyType = (() => {
    const bodyParam = (params as any)?.bodyType;
    
    if (!bodyParam) return undefined;
    if (typeof bodyParam === 'string') return bodyParam.toLowerCase();
    return bodyParam?.[0]?.toLowerCase();
  })();

  /* --------------------------------------------------
   * GENERIC PARSER
   * -------------------------------------------------- */

  const createMapParser = <T extends { name: string; isChecked: boolean }>(
    items: T[],
  ) =>
    createParser({
      parse(queryValue) {
        return items.map((item) => ({
          ...item,
          isChecked: queryValue
            .toLowerCase()
            .includes(item.name.toLowerCase()),
        }));
      },
      serialize(value) {
        return value
          .filter((item) => item.isChecked)
          .map((item) => item.name)
          .join(',');
      },
      eq(a, b) {
        return isFilterEqual(a, b);
      },
    });

  /* --------------------------------------------------
   * BODY TYPES (LANDING-AWARE DEFAULT)
   * -------------------------------------------------- */

  const mappedBodyTypes = bodyTypes.map((item) => ({
    ...item,
    isChecked:
      !searchParams.has('bodytype') && landingBodyType
        ? item.name.toLowerCase().replaceAll(' ', '-') === landingBodyType
        : false,
  }));

  const parseBodyTypes = createMapParser(mappedBodyTypes);

  const [queryBodyTypes, setQueryBodyTypes] = useQueryState(
    'bodytype',
    parseBodyTypes
      .withDefault(mappedBodyTypes)
      .withOptions({ startTransition, shallow: false }),
  );

  /* --------------------------------------------------
   * COLOURS
   * -------------------------------------------------- */

  const mappedColourTypes = colourTypes.map((item) => ({
    ...item,
    isChecked: false,
  }));

  const parseColourTypes = createMapParser(mappedColourTypes);

  const [queryColourTypes, setQueryColourTypes] = useQueryState(
    'colours',
    parseColourTypes
      .withDefault(mappedColourTypes)
      .withOptions({ startTransition, shallow: false }),
  );

  /* --------------------------------------------------
   * DOORS
   * -------------------------------------------------- */

  const mappedDoorTypes = doorTypes.map((item) => ({
    ...item,
    isChecked: false,
  }));

  const parseDoorTypes = createMapParser(mappedDoorTypes);

  const [queryDoorTypes, setQueryDoorTypes] = useQueryState(
    'doors',
    parseDoorTypes
      .withDefault(mappedDoorTypes)
      .withOptions({ startTransition, shallow: false }),
  );

  /* --------------------------------------------------
   * TOGGLE HANDLER (EXIT LANDING MODE)
   * -------------------------------------------------- */

  const toggleFilter = async (
    item: { name: string; isDisabled?: boolean },
    setter: (
      value: any[] | ((old: any[]) => any[] | null) | null,
      options?: Options,
    ) => Promise<URLSearchParams>,
    _filterType?: 'body' | 'colour' | 'door',
  ) => {
    if (item.isDisabled) return;

    // Reset page to 1 when any filter is changed
    resetPage();

    // Clear search box when any filter is deselected
    if (setSearchQuery && searchQuery) {
      searchClearedByUserRef.current = false;
      setSearchQuery?.(null);
    }
    
    if (pathname.includes('-bodytype')) {
      const qs = searchParams.toString();

      router.replace(qs ? `/used-cars/search?${qs}` : '/used-cars/search');
    }

    await setter((options) =>
      produce(options, (draft) => {
        draft?.forEach((opt) => {
          if (opt.name === item.name) {
            opt.isChecked = !opt.isChecked;
          }
        });
      }),
    );
  };

  /* --------------------------------------------------
   * RENDER
   * -------------------------------------------------- */

  return (
    <NewFilter
      label="Body"
      placement="bottom-start"
      count={count}
      onClear={() => {
        const hasBodyFilters =
          searchParams.has('bodytype') ||
          searchParams.has('colours') ||
          searchParams.has('doors');

        if (hasBodyFilters) {
          setQueryBodyTypes(null);
          setQueryColourTypes(null);
          setQueryDoorTypes(null);
          setSearchQuery?.(null);
          generate();
          return;
        }
        searchClearedByUserRef.current = true;
        landingAwareClear('two-step');
      }}
    >
      <Box className={styles.bodyTypeWrapper}>
        <Text className={styles.heading}>Body Types</Text>
        <Box className={styles.bodyList}>
          {queryBodyTypes.map((body) => (
            <Box
              key={body.id}
              onClick={() => toggleFilter(body, setQueryBodyTypes, 'body')}
              className={cn(
                styles.bodyTypeItem,
                body.isChecked && styles.bodyTypeItemSelected,
                body.isDisabled && styles.bodyTypeDisabled,
              )}
            >
              <Image
                src={getImageLink(body.image) || ''}
                alt={body.name}
                title={body.name}
                width={82}
                height={42}
                className={styles.bodyImage}
              />
              <Text className={styles.bodyTypeText}>
                {body.name}
                <Text as="span" className={styles.filterCount}>
                  ({body.count})
                </Text>
              </Text>
            </Box>
          ))}
        </Box>

        <Text className={styles.heading}>Colour</Text>
        <Box className={styles.bodyList}>
          {queryColourTypes.map((colour) => (
            <Box
              key={colour.id}
              onClick={() => toggleFilter(colour, setQueryColourTypes, 'colour')}
              className={cn(
                styles.bodyTypeItem,
                colour.isChecked && styles.bodyTypeItemSelected,
                colour.isDisabled && styles.bodyTypeDisabled,
              )}
            >
              <div
                className={cn(styles.colourCircle, {
                  [styles.withBorder as string]:
                    colour.name?.toLowerCase() === 'white',
                })}
                style={{ backgroundColor: colour.colour || 'white' }}
              />
              <Text className={styles.bodyTypeText}>
                {colour.name}
                <Text as="span" className={styles.filterCount}>
                  ({colour.count})
                </Text>
              </Text>
            </Box>
          ))}
        </Box>

        <Text className={styles.heading}>Doors</Text>
        <Box className={styles.bodyList}>
          {queryDoorTypes.map((door) => (
            <Box
              key={door.id}
              onClick={() => toggleFilter(door, setQueryDoorTypes)}
              className={cn(
                styles.bodyTypeItem,
                styles.doorItem,
                door.isChecked && styles.doorSelected,
                door.isDisabled && styles.bodyTypeDisabled,
              )}
            >
              <Text className={styles.bodyTypeText}>
                {door.name}
                <Text as="span" className={styles.filterCountDoor}>
                  ({door.count})
                </Text>
              </Text>
            </Box>
          ))}
        </Box>
      </Box>
    </NewFilter>
  );
};
